import { Button } from '@kanbu/chatbot-ui';
import type { CreateChatMessageDTO } from '@kanbu/schema/contracts';
import { ChatRole } from '@kanbu/schema/enums';
import { useMutation } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { useChatConfig } from '@/contexts/ChatConfigProvider';
import type { UseChatReturn } from '@/routes/chat/chatTypes';
import { aiCoreApi } from '@/services/aiCoreClient';
import { useBoundStore } from '@/store/store';

interface FAQItemProps {
  id: string;
  title: string;
  question: string;
  answer: string;
  childrenCount: number;
  insert: UseChatReturn['insert'];
}

export function FAQItem({
  id,
  title,
  question,
  answer,
  insert,
  childrenCount,
}: FAQItemProps) {
  const { chatId } = useChatConfig();
  const { threadId, setFAQ, setIsInFAQLoop } = useBoundStore(
    useShallow(state => ({
      threadId: state.threadId,
      setFAQ: state.setFAQ,
      setIsInFAQLoop: state.setIsInFAQLoop,
    })),
  );

  const { mutateAsync: increment } = useMutation({
    mutationFn: () =>
      aiCoreApi.faqs.increment({
        chatId,
        faqId: id,
      }),
  });

  const { mutateAsync: createMessages } = useMutation({
    mutationFn: async (messages: CreateChatMessageDTO[]) => {
      try {
        await aiCoreApi.threads.messages.create({
          chatId,
          threadId: threadId!,
          messages,
        });
      } catch (error) {
        console.error(error);
      }

      setIsInFAQLoop(true);
    },
  });

  const handleClick = async () => {
    // Insert question
    setFAQ(id, childrenCount);
    insert(question, {
      role: ChatRole.User,
    });

    await Promise.all([
      // Increase click count
      increment(),
      // Save to thread history
      threadId &&
        createMessages([
          {
            thread: threadId,
            role: ChatRole.User,
            content: question,
          },
          {
            thread: threadId,
            role: ChatRole.Assistant,
            content: answer,
          },
        ]),

      // Insert answer (with timeout to make it feel more real)
      insert(answer, { timeout: 500 }),
    ]);
  };

  if (!threadId) {
    return null;
  }

  return (
    <Button
      data-cta='chatbot-faq-button'
      type='button'
      circle
      variant='outline'
      onClick={handleClick}
      data-testid='faq-button'
    >
      {title}
    </Button>
  );
}
